import React, { FC, useEffect, useRef } from "react";
import Datamap from "datamaps";

interface CarteMondeProps {
  data: any;
  responsivite: {
    resp: number;
  };
}

const loadedScripts = new Set<string>();

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    /*
     * Le script est déjà chargé.
     */
    if (loadedScripts.has(src)) {
      resolve();
      return;
    }

    /*
     * Le script est peut-être déjà présent dans la page.
     */
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`
    );

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        loadedScripts.add(src);
        resolve();
        return;
      }

      existingScript.addEventListener(
        "load",
        () => {
          existingScript.dataset.loaded = "true";
          loadedScripts.add(src);
          resolve();
        },
        { once: true }
      );

      existingScript.addEventListener("error", reject, {
        once: true,
      });

      return;
    }

    const script = document.createElement("script");

    script.src = src;
    script.async = true;

    script.onload = () => {
      script.dataset.loaded = "true";
      loadedScripts.add(src);
      resolve();
    };

    script.onerror = reject;

    document.head.appendChild(script);
  });
};

const CarteMonde: FC<CarteMondeProps> = ({
  data,
  responsivite,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    const loadMap = async () => {
      if (!data || !mapContainer.current) {
        return;
      }

      try {
        await loadScript(
          "https://cdnjs.cloudflare.com/ajax/libs/d3/3.5.3/d3.min.js"
        );

        await loadScript(
          "https://cdnjs.cloudflare.com/ajax/libs/topojson/1.6.9/topojson.min.js"
        );

        if (cancelled || !mapContainer.current) {
          return;
        }

        /*
         * Suppression de l’ancienne carte avant recréation.
         * Cela empêche les SVG de se superposer.
         */
        mapContainer.current.innerHTML = "";
        mapInstance.current = null;

        mapInstance.current = new Datamap({
          element: mapContainer.current,

          /*
           * Fonction responsive native de Datamaps.
           */
          responsive: true,

          projection: "mercator",

          fills: {
            defaultFill: "#FFFFFF",
          },

          data,

          geographyConfig: {
  borderColor: "#DEDEDE",
  borderWidth: 1,

  /*
   * Désactivation du système de survol Datamaps.
   * Nous gérons nous-mêmes les couleurs et la popup dans done().
   */
  highlightOnHover: false,
  popupOnHover: false,
},

          done: (datamap: any) => {
  const d3Global = (window as any).d3;

  if (!d3Global || !mapContainer.current) {
    return;
  }

  /*
   * Création de notre propre tooltip.
   */
  const tooltip = d3Global
    .select(mapContainer.current)
    .append("div")
    .attr("class", "carteMondeTooltip")
    .style("position", "absolute")
    .style("display", "none")
    .style("pointer-events", "none")
    .style("z-index", "20");

  datamap.svg
    .selectAll(".datamaps-subunit")

    /*
     * Entrée sur un pays.
     */
    .on(
      "mouseenter.customHover",
      function (
        this: SVGPathElement,
        geo: any
      ) {
        const countryCode = geo?.id;
        const countryData = data?.[countryCode];

        const isVisited = Boolean(countryData?.fillColor);

        /*
         * On mémorise la couleur exacte avant le survol.
         */
        const element = d3Global.select(this);

        const originalColor =
          countryData?.fillColor || "#FFFFFF";

        element
          .attr("data-original-fill", originalColor)
          .interrupt()
          .style(
            "fill",
            isVisited
              ? "#FF747A"
              : "#FCEBEC"
          )
          .style(
            "stroke",
            isVisited
              ? "#FF4A52"
              : "#C8C8C8"
          )
          .style("stroke-width", "1.5px");

        /*
         * Le nom est affiché uniquement pour un pays visité.
         */
        if (countryData?.nom) {
          tooltip
            .html(
              `<strong>${countryData.nom}</strong>`
            )
            .style("display", "block");
        } else {
          tooltip.style("display", "none");
        }
      }
    )

    /*
     * Déplacement de la popup avec la souris.
     */
    .on(
      "mousemove.customHover",
      function () {
        if (
          tooltip.style("display") === "none"
        ) {
          return;
        }

        const mouse = d3Global.mouse(
          mapContainer.current
        );

        tooltip
          .style("left", `${mouse[0] + 14}px`)
          .style("top", `${mouse[1] + 14}px`);
      }
    )

    /*
     * Sortie du pays : restauration forcée de sa couleur.
     */
    .on(
      "mouseleave.customHover",
      function (
        this: SVGPathElement,
        geo: any
      ) {
        const countryCode = geo?.id;

        const originalColor =
          data?.[countryCode]?.fillColor ||
          "#FFFFFF";

        d3Global
          .select(this)
          .interrupt()
          .style("fill", originalColor)
          .style("stroke", "#DEDEDE")
          .style("stroke-width", "1px");

        tooltip.style("display", "none");
      }
    );
},
        });

        /*
         * On redimensionne la carte lorsque la fenêtre change.
         */
        const handleResize = () => {
          mapInstance.current?.resize?.();
        };

        window.addEventListener("resize", handleResize);

        /*
         * Premier recalcul après affichage.
         */
        window.setTimeout(handleResize, 0);

        return () => {
          window.removeEventListener(
            "resize",
            handleResize
          );
        };
      } catch (error) {
        console.error(
          "Erreur lors du chargement de la carte :",
          error
        );
      }
    };

    let removeResizeListener:
      | (() => void)
      | undefined;

    loadMap().then((cleanup) => {
      removeResizeListener = cleanup;
    });

    return () => {
      cancelled = true;

      if (removeResizeListener) {
        removeResizeListener();
      }

      mapInstance.current = null;

      if (mapContainer.current) {
        mapContainer.current.innerHTML = "";
      }
    };
  }, [
    data,

    /*
     * On dépend de la valeur primitive et non de l’objet complet.
     */
    responsivite.resp,
  ]);

  return (
    <div
      ref={mapContainer}
      className="carteMonde relative"
    />
  );
};

export default CarteMonde;